import * as inflected from 'inflected'
import {
  selectorTypes,
  inputTypes,
  stepTypes,
  dataTypes,
} from '@easybutton/constants'

import { getColumns } from './databases'
import { buildIndex } from './indexing'
import { sort } from './arrays'
import { getId } from './ids'

const OPERATIONS = [
  { label: 'Create', value: 'create' },
  { label: 'Update', value: 'update' },
  { label: 'Delete', value: 'delete' },
]

export const emptyDatabaseStep = databaseId => {
  return {
    id: getId(),
    stepType: stepTypes.DATABASE,
    databaseId,
  }
}

export const emptyAPIStep = () => {
  return {
    id: getId(),
    stepType: stepTypes.API,
  }
}

export const getOperationOptions = database => {
  let { schema } = database

  if (!Array.isArray(schema)) {
    return []
  }

  let tables = sort(schema, t => t.name)

  let options = tables.map(table => {
    let childOptions = OPERATIONS.map(({ label, value }) => ({
      label: `${label} ${table.singularName}`,
      value: JSON.stringify({
        table: table.name,
        operation: value,
      }),
    }))

    return {
      label: table.name,
      options: childOptions,
    }
  })

  return options
}

export const getFields = (database, tableName, opts = {}) => {
  const { simple = false, expandable = false } = opts
  const { schema } = database

  if (!Array.isArray(schema)) {
    return []
  }

  const table = schema.filter(table => table.name === tableName)[0]

  if (!table) {
    return []
  }

  let columns = getColumns(table, {
    primaryKey: true,
    password: true,
    action: true,
  })

  if (expandable) {
    columns = columns.filter(c =>
      [dataTypes.ARRAY, dataTypes.OBJECT].includes(c.dataType)
    )
  } else if (simple) {
    columns = columns.filter(
      c => ![dataTypes.ARRAY, dataTypes.OBJECT].includes(c.dataType)
    )
  }

  return columns
}

export const prepareStep = (stepData, action, opts) => {
  if (stepData.stepType === stepTypes.DATABASE) {
    let { database } = opts

    return prepareDatabaseStep(stepData, action, database)
  }
}

export const prepareDatabaseStep = (stepData, action, database) => {
  let { operationAndTable, selector, ...rest } = stepData

  // Fix operationAndTable
  let { operation, table: tableName } = JSON.parse(operationAndTable || '{}')

  let result = {
    ...rest,
    operation,
    table: tableName,
  }

  let inputs = [...(action.details.inputs || [])]

  let table = database.schema.filter(t => t.name === tableName)[0]

  // Fix selector
  if (!['update', 'delete'].includes(operation) || !table) {
    selector = undefined
  } else if (selector === 'newInput') {
    // Create new input
    const input = {
      id: getId(),
      type: inputTypes.PRIMARY_KEY,
      name: table.singularName || tableName,
      databaseId: database.id,
      table: tableName,
    }

    const index = getPrimaryKeyIndex(inputs)

    inputs.splice(index, 0, input)

    selector = {
      type: selectorTypes.INPUT,
      inputId: input.id,
    }
  } else if (selector) {
    selector = JSON.parse(selector)
  }

  result.selector = selector

  // Fix fields
  if (operation === 'delete' || !table) {
    delete result.fields
  } else {
    let fields = { ...result.fields }
    let tableFields = buildIndex(table.columns, col => col.name)

    for (let key of Object.keys(fields)) {
      if (!fields[key] || !tableFields[key]) {
        delete fields[key]
      }
    }

    result.fields = fields
  }

  return [result, inputs]
}

export const getPrimaryKeyIndex = inputs => {
  let index = 0

  for (
    let i = 0;
    inputs[i] && inputs[i].type === inputTypes.PRIMARY_KEY;
    i += 1
  ) {
    index = i
  }

  return index
}

export const prepareInitialValues = (step, database) => {
  let result = { ...step }

  if (step.stepType === stepTypes.DATABASE) {
    // Serialize selector
    result.selector = step.selector
      ? JSON.stringify(result.selector)
      : 'newInput'

    // Serialize operationAndTable
    result.operationAndTable =
      (result.table &&
        result.operation &&
        JSON.stringify({
          table: result.table,
          operation: result.operation,
        })) ||
      null

    if (!result.operation && !result.table) {
      let options = getOperationOptions(database)
      let group = options[0]

      if (group && group.options) {
        let option = group.options[0]
        result.operationAndTable = option && option.value
      }
    }

    delete result.operation
    delete result.table
  }

  return result
}

export const generateDatabaseActionName = step => {
  const table = inflected.titleize(
    inflected.singularize(inflected.underscore(step.table))
  )

  // Create
  if (step.operation === 'create') {
    return `Create ${table}`
  }

  // Update
  if (step.operation === 'update') {
    const keys = Object.keys(step.fields).filter(
      key => ![null, undefined, ''].includes(step.fields[key])
    )

    // Single field
    if (keys.length === 1) {
      return `Set ${inflected.titleize(inflected.underscore(keys[0]))}`
    }

    // Multiple fields
    return `Update ${table}`
  }

  // Delete
  if (step.operation === 'delete') {
    return `Delete ${table}`
  }
}

export const generateActionName = step => {
  if (step.stepType === stepTypes.DATABASE) {
    return generateDatabaseActionName(step)
  }
}
